// Container do Dashboard (§4.1 da spec) com DOIS modos: INTRADIÁRIO
// (1 dia + faixa de horas na URL, série HORÁRIA) e HISTÓRICO (janela
// ?startDate&?endDate, série por DIA/SEMANA). WL do seletor GLOBAL do
// portal (useAuth, "Todos" = sem query); métricas agrupadas em 2
// seções — Engajamento (funil Enviados→Entregues→Lidos + série +
// canais) e Operação (falhas/fila/agendados + providers + eventos).
// Tudo clicável → Consulta pré-filtrada na janela selecionada.
// Fonte: GET /notification-center/dashboards/overview.
import {
  ActivityIcon,
  HeartHandshakeIcon,
} from "lucide-react"
import { useTranslation } from "react-i18next"

import { useAuth } from "@/contexts/auth"
import { useNotificationCenterDashboardSearchParams } from "@/hooks"

import { useNotificationCenterDashboardOverview } from "../hooks"
import { DashboardChannelChart } from "./dashboard-channel-chart"
import { DashboardFailuresChart } from "./dashboard-failures-chart"
import { DashboardFunnel } from "./dashboard-funnel"
import { DashboardOperationKpis } from "./dashboard-operation-kpis"
import { DashboardPeriodFilter } from "./dashboard-period-filter"
import { DashboardSection } from "./dashboard-section"
import { DashboardSeriesChart } from "./dashboard-series-chart"
import { DashboardTopEventsChart } from "./dashboard-top-events-chart"

export const NotificationCenterDashboardOverview = () => {
  const { t } = useTranslation()
  const { selectedWhiteLabel } = useAuth()
  const { mode, overviewParams, linkRange, setHourRange } =
    useNotificationCenterDashboardSearchParams()

  const dashboardPrefix =
    "pages.notificationCenter.dashboardPage"

  // Params do modo vigente (intradiário: date/horas; histórico:
  // startDate/endDate — nunca os dois) + WL do seletor global
  // (ausente = "Todos", sem query).
  const { data, error, isLoading, refetch } =
    useNotificationCenterDashboardOverview({
      ...overviewParams,
      whiteLabelId: selectedWhiteLabel?.uuid,
    })

  // Deep-links da Consulta: janela resolvida em startDate/endDate.
  const range = linkRange

  // Granularidade do eixo da série vem do backend (hour/day/week).
  const interval = data?.period?.interval ?? "hour"

  // Rótulo do delta muda por modo: intradiário = vs dia anterior;
  // histórico = vs período anterior de mesmo tamanho.
  const deltaLabel = t(
    `${dashboardPrefix}.kpis.${
      mode === "historical"
        ? "deltaLabelHistorical"
        : "deltaLabel"
    }`
  )

  const hasError = Boolean(error)
  const commonChartProps = {
    isLoading,
    hasError,
    onRetry: () => refetch(),
    range,
  }

  return (
    <div className="space-y-6 @container">
      <header className="flex flex-col gap-1">
        <h1 className="text-2xl font-bold tracking-tight">
          {t(`${dashboardPrefix}.title`)}
        </h1>
        <p className="text-sm text-muted-foreground">
          {t(`${dashboardPrefix}.subtitle`)}
        </p>
      </header>
      <DashboardPeriodFilter />
      <DashboardSection
        icon={HeartHandshakeIcon}
        title={t(
          `${dashboardPrefix}.sections.engagement.title`
        )}
        description={t(
          `${dashboardPrefix}.sections.engagement.description`
        )}
      >
        {/* Série horária compacta em largura cheia no topo da seção;
            funil abaixo; canal e top 10 lado a lado no rodapé. */}
        <DashboardSeriesChart
          series={data?.series}
          interval={interval}
          isLoading={isLoading}
          hasError={hasError}
          onRetry={() => refetch()}
          onHourRangeSelect={(fromHour, toHour) =>
            setHourRange({ fromHour, toHour })
          }
        />
        <DashboardFunnel
          funnel={data?.funnel}
          kpis={data?.kpis}
          isLoading={isLoading}
          hasError={hasError}
          onRetry={() => refetch()}
          range={range}
          deltaLabel={deltaLabel}
        />
        <div className="grid grid-cols-2 gap-4">
          <DashboardChannelChart
            byChannel={data?.byChannel}
            {...commonChartProps}
          />
          <DashboardTopEventsChart
            topEvents={data?.topEvents}
            {...commonChartProps}
          />
        </div>
      </DashboardSection>
      <DashboardSection
        icon={ActivityIcon}
        title={t(
          `${dashboardPrefix}.sections.operation.title`
        )}
        description={t(
          `${dashboardPrefix}.sections.operation.description`
        )}
      >
        <DashboardOperationKpis
          kpis={data?.kpis}
          isLoading={isLoading}
          hasError={hasError}
          onRetry={() => refetch()}
          range={range}
          deltaLabel={deltaLabel}
        />
        <DashboardFailuresChart
          failuresByProvider={data?.failuresByProvider}
          {...commonChartProps}
        />
      </DashboardSection>
    </div>
  )
}
